import { mergeStyleSets } from "@fluentui/react";
import { LockSolidIcon } from "@fluentui/react-icons-mdl2";
import {
  Button,
  Chat,
  Dialog,
  EditIcon,
  Flex,
  Text,
  TrashCanIcon,
} from "@fluentui/react-northstar";
import React, { useMemo } from "react";
import Markdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import { appState } from "../../../AppState";
import { markdownComponents } from "../../utils/constants";
import { checkIfUserInMention } from "../../utils/helper";
import { InlineAttachmentImage } from "../EditTicketAttachments/NewAttachmentFlow/InlineAttachmentImage";
import { EditableTicketChatContent } from "./EditableTicketChat/EditableTicketChatContent";
import { renderAttachments } from "./EditableTicketChat/renderAttachments";
import { useDeleteComment } from "./EditableTicketChat/useDeleteComment";
import { useScrollIntoViewOnEdit } from "./EditableTicketChat/useScrollIntoViewOnEdit";
import { replaceTikitImgToMarkDown, resolveCommentDate } from "./TicketActivity";
import { useTicketChatContent } from "./useTicketChatContent";

const classNames = mergeStyleSets({
  commentWithAttachments: {
    background: "var(--mgt-theme-brand-background1)",
    ".ui-chat__message": {
      background: "var(--mgt-theme-brand-background1)",
      width: "100%",
      maxWidth: "100%",
      margin: 0,
    },
  },
  // Northstar reserves 100px on a message's far side - its `offset` - which is what stops a
  // reader's own comments from reaching the opposite edge. chatMessageStyle replaces that
  // margin with 8px on incoming comments, so the @mention badge stays beside the bubble
  // rather than 100px away, and the rest of the reservation is made here instead. Without it
  // an incoming comment renders 92px wider than the same comment from the reader. #35306
  //
  // Below 768px there is nothing to match: the rule below zeroes the reader's own offset
  // too, and a phone has no width to spare.
  incomingRow: {
    paddingRight: '92px',
    '@media (max-width: 767px)': {
      paddingRight: 0,
    },
  },
  commentRow: {
    ".comment-actions": { visibility: "hidden" },
    "&:hover .comment-actions": { visibility: "visible" },
    // On touch devices there is no hover, so keep the edit/delete actions
    // always visible instead of hiding them behind a hover state.
    "@media (hover: none)": {
      ".comment-actions": { visibility: "visible" },
    },
    // On mobile widen the bubble so the full date fits on one line and
    // tighten the edit/delete actions so they sit right beside the date.
    "@media (max-width: 767px)": {
      ".ui-chat__message": { maxWidth: "100%", marginLeft: "0px" },
      ".comment-date": { whiteSpace: "nowrap" },
      ".comment-actions": {
        gap: 0,
        "> *": { margin: 0 },
      },
    },
  },
});

type TicketChatContentProps = {
  comment: any;
  ticketId: number;
  isEndUserPage: boolean;
  canEditOwnComments?: boolean;
  canDeleteOwnComments?: boolean;
  chatMessageStyle?: () => React.CSSProperties;
  position: string;
  attachments: React.ReactNode;
  userInfoShow: JSX.Element;
  onDeleteSuccess?: () => void;
  onCommentUpdated?: () => void;
};

const TicketChatContent: React.FC<TicketChatContentProps> = ({
  comment,
  ticketId,
  isEndUserPage,
  canEditOwnComments = false,
  canDeleteOwnComments = false,
  chatMessageStyle,
  position,
  attachments,
  userInfoShow,
  onDeleteSuccess,
  onCommentUpdated,
}) => {
  const currentState = appState();
  const { isDialogOpen, openDeleteDialog, closeDeleteDialog, deleteComment: handleDeleteComment } = useDeleteComment();
  const {
    assignees,
    commentBody,
    commentIsPublic,
    commentFileAttachments,
    attachmentsModified,
    isEditing,
    setIsEditing,
    isEditModeReady,
    message,
    setMessage,
    editedAttachmentsList,
    setEditedAttachmentsList,
    isPublic,
    setIsPublic,
    cancelEdit,
    handleUpdateMessage,
    handleSendMessage,
    updateLoading,
    actionStatus,
    isUploading,
    uploadProgress,
    hasChanges,
  } = useTicketChatContent(comment, currentState.usersInRole, ticketId, isEndUserPage, onCommentUpdated);

  const containerRef = useScrollIntoViewOnEdit(isEditing);

  // Check if comment has been edited by comparing CreatedDate and ModifiedDate
  const isEdited = useMemo(() => {
    if (!comment?.CreatedDate || !comment?.ModifiedDate) return false;
    const createdDate = new Date(comment.CreatedDate).getTime();
    const modifiedDate = new Date(comment.ModifiedDate).getTime();
    // Consider it edited if ModifiedDate is at least 1 second after CreatedDate
    return modifiedDate > createdDate + 1000;
  }, [comment?.CreatedDate, comment?.ModifiedDate]);

  let attachmentBlockContent: React.ReactNode = null;
  if (attachmentsModified) {
    attachmentBlockContent = renderAttachments(commentBody, commentFileAttachments || []);
  } else if (commentFileAttachments && commentFileAttachments.length > 0) {
    attachmentBlockContent = attachments;
  }

  return (
    <div ref={containerRef} className={`${isEditing ? "w-full min-w-0 max-w-full mobile-full-editor" : ""}`}>
      {isEditing && isEditModeReady ? (
        <EditableTicketChatContent
          message={message}
          setMessage={setMessage}
          assignees={assignees}
          editedAttachmentsList={editedAttachmentsList}
          setEditedAttachmentsList={setEditedAttachmentsList}
          onCancel={cancelEdit}
          comment={comment}
          handleUpdateMessage={handleUpdateMessage}
          handleSendMessage={handleSendMessage}
          isPublic={isPublic}
          setIsPublic={setIsPublic}
          loading={updateLoading || isUploading}
          actionStatus={actionStatus}
          uploadProgress={uploadProgress}
          hasChanges={hasChanges}
        />
      ) : (
        <Flex className={`w-full items-center ${classNames.commentRow}`}>
          <div className="flex flex-col w-full">
            {isEdited && position === "end" && (
              <div className="flex justify-end pr-1">
                <Text size="smaller" content="Edited" color="brand" />
              </div>
            )}
          <Flex
            column
            className={`${(commentFileAttachments?.length ?? 0) > 0
                ? classNames.commentWithAttachments
                : ""
              } w-full`}
          >
            <div className="relative w-full">
              <Flex
                className={`${position === "end"
                    ? `items-center justify-end`
                    : `items-center ${classNames.incomingRow}`
                  }`}
              >
                <Chat.Message
                style={chatMessageStyle()}
                content={
                  <Markdown remarkPlugins={[remarkGfm, remarkBreaks]} components={{ ...markdownComponents, img: InlineAttachmentImage }}>
                    {
                      replaceTikitImgToMarkDown(commentBody, commentFileAttachments)
                    }
                  </Markdown>
                }
                author={userInfoShow}
                header={
                  <Flex gap="gap.small" className="justify-between">
                    <Text
                      size="small"
                      timestamp
                      color="grey"
                      content={
                        <div className="flex items-center">
                          <span className="comment-date leading-5 pr-2">
                            {resolveCommentDate(comment.CreatedDate)}
                          </span>
                          {!commentIsPublic && (
                            <LockSolidIcon className="text-private" />
                          )}
                        </div>
                      }
                    />
                    {position === "end" && (
                      <Flex gap="gap.smaller" className="comment-actions transform scale-75 items-center">
                        {canEditOwnComments && (
                          <Flex.Item>
                            <Button
                              size="small"
                              icon={<EditIcon />}
                              text
                              iconOnly
                              title="Edit"
                              onClick={() => setIsEditing(true)}
                            />
                          </Flex.Item>
                        )}
                        {canDeleteOwnComments && (
                          <Flex.Item>
                            <Button
                              size="small"
                              primary
                              icon={<TrashCanIcon />}
                              text
                              iconOnly
                              title="Delete"
                              onClick={openDeleteDialog}
                            />
                          </Flex.Item>
                        )}
                      </Flex>
                    )}
                  </Flex>
                }
                {...(position === "end" && { mine: true })}
                />

                {(commentFileAttachments?.length ?? 0) === 0 &&
                  position !== "end" &&
                  checkIfUserInMention(commentBody.toLowerCase()) && (
                    <Text content="@" atMention="me" size="larger" />
                  )}
              </Flex>
            </div>
            <div>
              {/* If attachments were modified, render our own based on commentFileAttachments,
                  otherwise use the prop from parent */}
              {attachmentBlockContent}
            </div>
          </Flex>
          </div>

          {commentFileAttachments &&
            commentFileAttachments.length > 0 &&
            position !== "end" &&
            checkIfUserInMention(commentBody.toLowerCase()) && (
              <Text
                content="@"
                atMention="me"
                size="larger"
                className="pl-2"
              />
            )}
        </Flex>
      )}
      <Dialog
        open={isDialogOpen}
        onCancel={closeDeleteDialog}
        cancelButton="Cancel"
        confirmButton="Delete"
        onConfirm={() => handleDeleteComment(comment.Id, onDeleteSuccess, commentFileAttachments || comment?.FileAttachments || [])}
        content="Are you sure you want to delete this comment?"
        header="Delete Comment"
        style={{ maxWidth: '500px' }}
      />
    </div>
  );
};

export default TicketChatContent;
